import React, { useMemo, useState } from 'react';
import Modal from '../../../ui-kit/Modal';
import Button from '../../../ui-kit/Button';
import { AlertTriangle, Ban, Phone } from 'lucide-react';
import { can } from '../../../core/permissions';
import { CALL_TYPES, CALL_RESULTS, CUSTOMER_MOODS, callWarning, isDND } from '../logic/telesales';
import { TelesalesService } from '../services/telesalesService';

const inputCls = 'w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm';

/**
 * `attempt` says how this form was reached, because "log a call" means three different
 * things and the record should not blur them:
 *   { placed: true }                 the system dialled — record what happened.
 *   { placed: false, error }         the dial failed; the agent may have called by hand.
 *   { placed: false, followUpOnly }  no call at all, just scheduling a follow-up.
 * Omitted entirely for callers that have not been updated yet (the form behaves as before).
 */
const CallLogModal = ({ isOpen, onClose, customer, currentUser, onSaved, onOrdered, attempt, onRetryDial }) => {
    const mobile = customer?.primaryMobile || customer?.mobile;
    const warning = useMemo(() => callWarning(customer?.callHistory), [customer]);
    const dnd = isDND(customer);
    const canOverrideDnd = can(currentUser?.role, 'canOverrideDND');

    const [confirmed, setConfirmed] = useState(!warning.warn);
    const [busy, setBusy] = useState(false);
    const [f, setF] = useState({ callType: CALL_TYPES[0], result: '', remark: '', followUpAt: '', assignedTo: '', productDiscussed: '', offerDiscussed: '', customerMood: '' });
    const set = (k, v) => setF(s => ({ ...s, [k]: v }));

    const blockedByDnd = dnd && !canOverrideDnd;

    const save = async () => {
        if (!f.result) { alert('Select a call result.'); return; }
        setBusy(true);
        try {
            const r = await TelesalesService.addCallLog(mobile, customer?.name, f, currentUser);
            onSaved?.();
            if (r.ordered) onOrdered?.(r.callId);
            onClose();
        } catch (e) { alert(e.message); } finally { setBusy(false); }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={attempt?.followUpOnly ? 'Create Follow-up' : attempt?.placed ? 'Call in progress' : 'Log Call'} className="max-w-xl">
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                {attempt?.placed && (
                    <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-lg p-2">
                        <Phone size={16} /> Dialing <b>{mobile}</b> on your phone. Record how it went once you hang up.
                    </div>
                )}
                {attempt && !attempt.placed && !attempt.followUpOnly && (
                    <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-lg p-2 space-y-1.5">
                        <div className="flex items-start gap-2"><AlertTriangle size={16} className="mt-0.5 shrink-0" />
                            <span>The call could not be placed from here{attempt.error ? ` — ${attempt.error}` : ''}. Only record it below if you actually called {mobile} yourself.</span></div>
                        {onRetryDial && <Button size="sm" variant="outline" onClick={onRetryDial}>Try dialing again</Button>}
                    </div>
                )}
                {attempt?.followUpOnly && (
                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 text-slate-600 text-sm rounded-lg p-2">
                        No call is being placed — this only schedules a follow-up.
                    </div>
                )}
                {dnd && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-2">
                        <Ban size={16} /> This customer is marked <b>Do Not Disturb</b>. {canOverrideDnd ? 'Admin override allowed.' : 'Only an admin can call.'}
                    </div>
                )}
                {warning.warn && !confirmed && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className="flex items-start gap-2 text-amber-800 text-sm"><AlertTriangle size={16} className="mt-0.5" /> {warning.message}</div>
                        <div className="flex justify-end gap-2 mt-2">
                            <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
                            <Button size="sm" onClick={() => setConfirmed(true)}>Call anyway</Button>
                        </div>
                    </div>
                )}

                {confirmed && !blockedByDnd && (
                    <>
                        <div className="grid grid-cols-2 gap-2">
                            <label className="text-xs text-slate-500">Call type
                                <select className={inputCls} value={f.callType} onChange={e => set('callType', e.target.value)}>{CALL_TYPES.map(t => <option key={t}>{t}</option>)}</select></label>
                            <label className="text-xs text-slate-500">Result *
                                <select className={inputCls} value={f.result} onChange={e => set('result', e.target.value)}><option value="">Select…</option>{CALL_RESULTS.map(t => <option key={t}>{t}</option>)}</select></label>
                            <label className="text-xs text-slate-500">Product discussed
                                <input className={inputCls} value={f.productDiscussed} onChange={e => set('productDiscussed', e.target.value)} /></label>
                            <label className="text-xs text-slate-500">Offer discussed
                                <input className={inputCls} value={f.offerDiscussed} onChange={e => set('offerDiscussed', e.target.value)} /></label>
                            <label className="text-xs text-slate-500">Customer mood
                                <select className={inputCls} value={f.customerMood} onChange={e => set('customerMood', e.target.value)}><option value="">—</option>{CUSTOMER_MOODS.map(t => <option key={t}>{t}</option>)}</select></label>
                            <label className="text-xs text-slate-500">Assigned to
                                <input className={inputCls} value={f.assignedTo} onChange={e => set('assignedTo', e.target.value)} /></label>
                        </div>
                        <label className="text-xs text-slate-500 block">Call remark
                            <textarea className={inputCls} rows={2} value={f.remark} onChange={e => set('remark', e.target.value)} /></label>
                        <label className="text-xs text-slate-500 block">Next follow-up date &amp; time
                            <input type="datetime-local" className={inputCls} value={f.followUpAt} onChange={e => set('followUpAt', e.target.value)} /></label>
                        {f.result === 'Ordered' && <p className="text-xs text-green-600">On save, the Add Order form will open.</p>}
                        <div className="flex justify-end gap-2 pt-1">
                            <Button variant="outline" onClick={onClose}>Cancel</Button>
                            <Button onClick={save} disabled={busy}><Phone size={15} /> Save Call</Button>
                        </div>
                    </>
                )}
                {blockedByDnd && <p className="text-sm text-slate-500 text-center py-4">Calling is blocked for DND customers without admin override.</p>}
            </div>
        </Modal>
    );
};

export default CallLogModal;
